import React, { useState, useEffect } from 'react';
import { securitySettingsService } from '../services/securitySettingsService';
import { supabase } from '../lib/supabase';
import { PermissionGate } from '../components/ui/PermissionGate';


/**
 * Permission Enforcement Wrapper
 * Wraps service methods with dynamic permission checking
 */
export const withPermissionCheck = (feature, requiredAction = 'read') => {
  return (serviceMethod) => {
    return async (...args) => {
      // Get current user
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        throw new Error('Authentication required');
      }

      // Check permission using dynamic security settings
      const hasPermission = await securitySettingsService?.checkFeaturePermission(
        user?.id,
        feature
      );

      if (!hasPermission) {
        throw new Error(`Access denied: You do not have permission to ${requiredAction} ${feature}`);
      }

      // Execute the original service method
      return await serviceMethod(...args);
    };
  };
};

/**
 * Create permission-aware service wrapper
 * @param {string} feature - Feature name (e.g., 'services', 'users', 'billing')
 * @param {Object} serviceMethods - Object containing service methods
 * @returns {Object} Wrapped service with permission checks
 */
export const createPermissionAwareService = (feature, serviceMethods) => {
  const wrappedService = {};

  Object.keys(serviceMethods)?.forEach(methodName => {
    const method = serviceMethods?.[methodName];

    // Determine action type from method name
    let action = 'read';
    if (methodName?.startsWith('create') || methodName?.startsWith('add')) {
      action = 'create';
    } else if (methodName?.startsWith('update') || methodName?.startsWith('edit')) {
      action = 'update';
    } else if (methodName?.startsWith('delete') || methodName?.startsWith('remove')) {
      action = 'delete';
    } else if (methodName?.startsWith('get') || methodName?.startsWith('fetch') || methodName?.startsWith('list')) {
      action = 'read';
    }

    // Wrap method with permission check
    wrappedService[methodName] = withPermissionCheck(feature, action)(method);
  });

  return wrappedService;
};

/**
 * Check if current user has permission for a feature
 * @param {string} feature - Feature name
 * @returns {Promise<boolean>}
 */
export const checkCurrentUserPermission = async (feature) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return false;

    return await securitySettingsService?.checkFeaturePermission(user?.id, feature);
  } catch (error) {
    console.error('Permission check failed:', error);
    return false;
  }
};

/**
 * Get all features current user has access to
 * @returns {Promise<Array<string>>}
 */
export const getCurrentUserFeatures = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return [];

    const features = securitySettingsService?.getAvailableFeatures();
    const allowedFeatures = [];

    for (const feature of features) {
      const hasAccess = await securitySettingsService?.checkFeaturePermission(user?.id, feature);
      if (hasAccess) {
        allowedFeatures?.push(feature);
      }
    }

    return allowedFeatures;
  } catch (error) {
    console.error('Failed to get user features:', error);
    return [];
  }
};

/**
 * React hook for permission checking
 * Usage: const canAccessServices = usePermission('services');
 */
export const usePermission = (feature) => {
  const [hasPermission, setHasPermission] = React.useState(false);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const checkPermission = async () => {
      try {
        setLoading(true);
        const allowed = await checkCurrentUserPermission(feature);
        setHasPermission(allowed);
      } catch (error) {
        console.error('Permission check error:', error);
        setHasPermission(false);
      } finally {
        setLoading(false);
      }
    };

    checkPermission();
  }, [feature]);

  return { hasPermission, loading };
};

/**
 * Permission Gate Component
 * Usage: <PermissionGate feature="services">...</PermissionGate>
 */
export const FeaturePermissionGate = ({ feature, children, fallback = null }) => {
  const { hasPermission, loading } = usePermission(feature);

  if (loading) return null;
  if (!hasPermission) return fallback;
  return children;
};